import { useMemo } from 'react';
import { useQuery } from 'urql';

import { PagePaths, WILSON_CLIENT_NAMES } from '@revelio/core';
import {
  DeliveryStatus,
  GetReportConfig,
  type GetReportConfigQuery,
} from '@revelio/data-access';

import { useBrand } from '../../../shared/branding/brand-provider';
import { usePageManager } from '../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { getLocalTemplates } from '../../CustomDash/utils/page-config-lookup';
import type { UserOption } from '../../DataBuilder/components/user-selector';
import {
  getUnifiedReportTypeDisplayText,
  useAllReports,
} from '../hooks/use-all-reports';
export const useReportTableData = (
  selectedUserOption: UserOption | undefined
) => {
  const { reports, fetching, executeQuery } = useAllReports({
    userId: selectedUserOption?.value,
  });
  const [{ data: config, fetching: configFetching }] =
    useQuery<GetReportConfigQuery>({
      query: GetReportConfig,
    });
  const { getPage } = usePageManager();
  const localTemplates = useMemo(() => getLocalTemplates(), []);
  const brand = useBrand();
  const isWilsonBrand = WILSON_CLIENT_NAMES.has(brand.clientName);

  const data = reports.map((report) => {
    const isCustomReport = report?.reportType === 'CustomReport';
    const status =
      isCustomReport || report?.reportType === 'GeneratedByRevelio'
        ? DeliveryStatus.Finished
        : report?.reportBuilderData?.status;
    const baseViewLink = `/${PagePaths.REPORTS}/${report?.id}`;
    let viewReportLink: string | null = null;
    if (isCustomReport) {
      viewReportLink = report?.link ?? null;
    } else if (status === DeliveryStatus.Finished && baseViewLink) {
      const userId = selectedUserOption?.value || '';
      const coverParam = isWilsonBrand ? '&cover=wilson' : '';
      viewReportLink = `${baseViewLink}?userId=${userId}${coverParam}`;
    }

    const reportTypeDisplay = (() => {
      if (report?.reportType === 'CustomReport') {
        const templateId = report.customReportTemplateId;
        if (templateId) {
          const userPage = getPage(templateId);
          const userName = userPage?.name?.trim();
          if (userName) return userName;
          const localName = localTemplates
            .find((t) => t.id === templateId)
            ?.name?.trim();
          if (localName) return localName;
        }
        return getUnifiedReportTypeDisplayText('CustomReport', config);
      }
      if (report?.reportType) {
        return getUnifiedReportTypeDisplayText(report.reportType, config);
      }
      return '';
    })();

    return {
      id: report?.id,
      name: report?.name,
      client: report?.reportBuilderData?.client || 'Revelio Labs',
      created_at:
        report?.createdAt || report?.reportBuilderData?.created_at || null,
      updated_at:
        report?.updatedAt || report?.reportBuilderData?.updated_at || null,
      status,
      reportType: reportTypeDisplay,
      viewReportLink,
    };
  });

  return {
    data,
    fetching: fetching || configFetching,
    refresh: executeQuery,
  };
};
